import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBoolean } from '@app-shared/decorators/coerce-boolean.decorator';

let nextToggleSwitchId = 0;

@Component({
	selector: 'app-toggle-switch',
	templateUrl: './toggle-switch.component.html',
	styleUrls: ['./toggle-switch.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ToggleSwitchComponent),
			multi: true
		}
	]
})
export class ToggleSwitchComponent implements ControlValueAccessor {
	@Input() label?: string;
	@Input() labelKey?: string;
	@coerceBoolean() @Input() active = false;
	@coerceBoolean() @Input() disabled = false;

	@Output() readonly activeChange = new EventEmitter<boolean>();
	@Output() readonly switchChange = new EventEmitter<boolean>();

	public readonly labelId = `toggle-switch-label-${nextToggleSwitchId++}`;

	private onChange: (value: boolean) => void = () => {};
	private onTouched: () => void = () => {};

	constructor(private readonly cdr: ChangeDetectorRef) {}

	writeValue(value: boolean | null | undefined): void {
		this.active = !!value;
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: boolean) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	toggle(): void {
		if (this.disabled) {
			return;
		}

		this.updateValue(!this.active);
	}

	private updateValue(value: boolean): void {
		this.active = value;
		this.onChange(this.active);
		this.onTouched();
		this.activeChange.emit(this.active);
		this.switchChange.emit(this.active);
		this.cdr.markForCheck();
	}
}
